/** @format */

/* Data-point field types — the single source of truth shared by the form-field
   creator dropdown and the workflow builder palette. */

export type FieldType =
  | 'text'
  | 'longtext'
  | 'number'
  | 'dropdown'
  | 'date'
  | 'daterange'
  | 'toggle'
  | 'file'
  | 'timestamp'
  | 'refnum'
  | 'duration'
  | 'fraction';

export interface FieldTypeMeta {
  label: string;
  icon: string;
  group: 'input' | 'auto';
  defaultLabel: string;
  auto?: boolean;
}

export interface DataPointTypeEntry extends FieldTypeMeta {
  /** Form-field creator dropdown id; null when not yet creatable as a data point. */
  id: number | null;
  /** Canonical `data_type` string persisted on a DataPoint. */
  dataPointType: string;
  /** Workflow-builder field key; null when the type doesn't render in the panel. */
  fieldType: FieldType | null;
  panelEnabled: boolean;
  dataPointCreatable: boolean;
}

/** Shape consumed by the form-field creator's "Form Field Type" dropdown. */
export interface DataPointTypeOption {
  id: number;
  dataPointType: string;
  isDeleted: boolean;
}

export const DATA_POINT_TYPES: DataPointTypeEntry[] = [
  { id: 3, dataPointType: 'Text (Short)', fieldType: 'text', panelEnabled: true, dataPointCreatable: true,
    label: 'Short Text', icon: 'type', group: 'input', defaultLabel: 'Text Field' },
  { id: 9, dataPointType: 'Text (Long)', fieldType: 'longtext', panelEnabled: true, dataPointCreatable: true,
    label: 'Long Text', icon: 'align-left', group: 'input', defaultLabel: 'Notes' },
  { id: 1, dataPointType: 'Number', fieldType: 'number', panelEnabled: true, dataPointCreatable: true,
    label: 'Number', icon: 'hash', group: 'input', defaultLabel: 'Number' },
  { id: 5, dataPointType: 'Dropdown List', fieldType: 'dropdown', panelEnabled: true, dataPointCreatable: true,
    label: 'Dropdown', icon: 'chevron-down', group: 'input', defaultLabel: 'Select…' },
  { id: 8, dataPointType: 'Date', fieldType: 'date', panelEnabled: true, dataPointCreatable: true,
    label: 'Date', icon: 'calendar', group: 'input', defaultLabel: 'Date' },
  { id: 11, dataPointType: 'Boolean', fieldType: 'toggle', panelEnabled: true, dataPointCreatable: true,
    label: 'Boolean / Toggle', icon: 'toggle-right', group: 'input', defaultLabel: 'Toggle' },
  { id: 12, dataPointType: 'Date Range', fieldType: 'daterange', panelEnabled: true, dataPointCreatable: true,
    label: 'Date Range', icon: 'calendar-range', group: 'input', defaultLabel: 'Date Range' },
  { id: 13, dataPointType: 'Timestamp (Auto)', fieldType: 'timestamp', panelEnabled: true, dataPointCreatable: true,
    label: 'Timestamp (auto)', icon: 'clock', group: 'auto', defaultLabel: 'Submitted At', auto: true },
  { id: 14, dataPointType: 'Reference Number', fieldType: 'refnum', panelEnabled: true, dataPointCreatable: true,
    label: 'Reference Number (auto)', icon: 'tag', group: 'auto', defaultLabel: 'Reference', auto: true },
  { id: 15, dataPointType: 'File Upload', fieldType: 'file', panelEnabled: true, dataPointCreatable: true,
    label: 'File Attachment', icon: 'paperclip', group: 'input', defaultLabel: 'Attachments' },
  { id: 16, dataPointType: 'Duration', fieldType: 'duration', panelEnabled: true, dataPointCreatable: true,
    label: 'Duration Calculator', icon: 'sigma', group: 'auto', defaultLabel: 'Duration (days)', auto: true },

  // Creator-only; no panel rendering yet. "Timestamp" here is a manual
  // date/time capture, distinct from the panel's auto "Submitted At" above.
  { id: 6, dataPointType: 'Fraction', fieldType: 'fraction', panelEnabled: true, dataPointCreatable: true,
    label: 'Fraction', icon: 'divide', group: 'input', defaultLabel: 'Fraction' },
  { id: 7, dataPointType: 'List (Single Selection)', fieldType: null, panelEnabled: false, dataPointCreatable: true,
    label: 'List (Single Selection)', icon: 'list', group: 'input', defaultLabel: 'Select one' },
  { id: 2, dataPointType: 'List (Multiple Selection)', fieldType: null, panelEnabled: false, dataPointCreatable: true,
    label: 'List (Multiple Selection)', icon: 'list-checks', group: 'input', defaultLabel: 'Select any' },
  { id: 10, dataPointType: 'GPS Capture', fieldType: null, panelEnabled: false, dataPointCreatable: true,
    label: 'GPS Capture', icon: 'map-pin', group: 'input', defaultLabel: 'Location' },
  { id: 4, dataPointType: 'Timestamp', fieldType: null, panelEnabled: false, dataPointCreatable: true,
    label: 'Timestamp', icon: 'clock', group: 'input', defaultLabel: 'Date & Time' },
];

/** Metadata for every field type, keyed by FieldType — used by makeField and field rendering. */
export const FIELD_TYPES: Record<FieldType, FieldTypeMeta> = DATA_POINT_TYPES
  .filter((e): e is DataPointTypeEntry & { fieldType: FieldType } => e.fieldType !== null)
  .reduce((acc, e) => {
    acc[e.fieldType] = {
      label: e.label,
      icon: e.icon,
      group: e.group,
      defaultLabel: e.defaultLabel,
      ...(e.auto ? { auto: true } : {}),
    };
    return acc;
  }, {} as Record<FieldType, FieldTypeMeta>);

/** Workflow builder palette tiles for a group — only types currently enabled for the panel. */
export function panelFieldTypes(group: 'input' | 'auto'): { key: FieldType; meta: FieldTypeMeta }[] {
  return DATA_POINT_TYPES
    .filter((e): e is DataPointTypeEntry & { fieldType: FieldType } =>
      e.panelEnabled && e.fieldType !== null && e.group === group)
    .map((e) => ({ key: e.fieldType, meta: FIELD_TYPES[e.fieldType] }));
}

/** Form-field creator view: the "Form Field Type" dropdown options. */
export function dataPointTypes(): DataPointTypeOption[] {
  return DATA_POINT_TYPES
    .filter((e) => e.dataPointCreatable && e.id !== null)
    .map((e) => ({ id: e.id as number, dataPointType: e.dataPointType, isDeleted: false }));
}
